import { observer } from "mobx-react";
import type React from "react";
import { Trans, useTranslation } from "react-i18next";

import { TeacherModals } from "constants/modals-constants";
import { TEACHER_HOME_PAGE_ROUTES } from "constants/routes";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useGroups } from "hooks/useGroups";
import { useModal } from "hooks/useModal";
import { useRedirect } from "hooks/useRedirect";

import { ModalRenderType } from "components/Modal/Modal.model";
import { Note } from "components/Note/Note";

import { Heading2 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { CssButton, CssFlex } from "styles/helpers/layout";

import { StyledModal } from "./GroupDeletionModal.styled";

interface ConfirmGroupDeletionModalProps {
  groupId: number;
  groupName: string;
}

export const ConfirmGroupDeletionModal: React.FC<ConfirmGroupDeletionModalProps> = observer(
  ({ groupId, groupName }) => {
    const modalId = TeacherModals.ConfirmGroupDeletion;

    const { t } = useTranslation("groups");
    const { redirectToUrl } = useRedirect();
    const { closeModal, isModalOpen } = useModal();
    const { getStudentTermByDomain } = useDomainHandler();
    const { deleteUserGroup, deletingGroup: isDeletingGroup } = useGroups();

    const isModalCurrentlyOpen = isModalOpen(modalId);

    // Delete group and redirect back to group list page
    const onConfirmDeleteClick = () => {
      deleteUserGroup(groupId).then(() => {
        closeModal();
        redirectToUrl(TEACHER_HOME_PAGE_ROUTES.GROUPS);
      });
    };

    return (
      <StyledModal
        dataCy={modalId}
        isOpen={isModalCurrentlyOpen}
        renderToElementType={ModalRenderType.REACT_PORTAL}
        onClose={closeModal}
      >
        <CssFlex flexDirection="column" gap={1} padding="2rem">
          <Heading2>{t("groups:groupDetails.confirmGroupDeletionModal.title")}</Heading2>
          <ParagraphLarge>
            <Trans
              components={{ groupNameTag: <b data-cy="target-group-name" /> }}
              i18nKey="groups:groupDetails.confirmGroupDeletionModal.description"
              t={t}
              values={{
                groupName,
                studentTerm: getStudentTermByDomain({
                  usePlural: true,
                }),
              }}
            />
          </ParagraphLarge>
          <Note variant="error">{t("groups:groupDetails.confirmGroupDeletionModal.alert")}</Note>
          <CssFlex justifyContent="space-between" marginTop={2} gap={1}>
            <CssButton disabled={!!isDeletingGroup} onClick={closeModal}>
              {t("groups:groupDetails.confirmGroupDeletionModal.cancelButton")}
            </CssButton>
            <CssButton
              data-cy="confirm-group-delete-button"
              variant="error"
              isLoading={!!isDeletingGroup}
              onClick={onConfirmDeleteClick}
            >
              {t("groups:groupDetails.confirmGroupDeletionModal.confirmButton")}
            </CssButton>
          </CssFlex>
        </CssFlex>
      </StyledModal>
    );
  },
);
